import type { CollectionEntry, CollectionLot, RiftboundCard } from '../types';
import { clampLotsToQuantities } from './collectionLots';

/** A printed language a collector can count and save separately. */
export type CollectionCopyLanguage = 'en' | 'zh' | 'ko';

/** Which printed language the collection page is counting. `all` is every copy. */
export type CollectionLanguageScope = 'all' | CollectionCopyLanguage;

export function parseCollectionLanguage(raw: string | null): CollectionLanguageScope {
  if (raw === 'en' || raw === 'zh' || raw === 'ko') return raw;
  return 'all';
}

export function parseCollectionCopyLanguage(raw: unknown): CollectionCopyLanguage {
  if (raw === 'zh' || raw === 'ko') return raw;
  return 'en';
}

function lotQuantity(lot: CollectionLot): number {
  return Math.max(0, Math.floor(lot.quantity) || 0);
}

function sumLots(lots: readonly CollectionLot[]): number {
  return lots.reduce((sum, lot) => sum + lotQuantity(lot), 0);
}

function isForeignLot(lot: CollectionLot): boolean {
  return Boolean(lot.language && lot.language !== 'en');
}

/** Copies stored on a non-English lot. English stays the unmarked remainder. */
export function nonEnglishLotQuantity(lots: CollectionLot[] | undefined): number {
  return sumLots((lots ?? []).filter(isForeignLot));
}

export function languageLotQuantity(
  lots: CollectionLot[] | undefined,
  language: 'zh' | 'ko' | 'fr',
): number {
  return sumLots((lots ?? []).filter((lot) => lot.language === language));
}

/** Unmarked copies plus English lots. A Chinese-only stack is zero. */
export function englishCopyQuantity(
  entry: Pick<CollectionEntry, 'quantity' | 'lots'> | undefined,
): number {
  if (!entry || entry.quantity <= 0) return 0;
  return Math.max(0, entry.quantity - nonEnglishLotQuantity(entry.lots));
}

/** The English card has a Simplified Chinese face beside it. */
export function cardHasChineseFace(card: Pick<RiftboundCard, 'locales'>): boolean {
  const zh = card.locales?.zh;
  return Boolean(zh && (zh.name || zh.imageUrl));
}

/** The English card has a Korean face beside it. */
export function cardHasKoreanFace(card: Pick<RiftboundCard, 'locales'>): boolean {
  const ko = card.locales?.ko;
  return Boolean(ko && (ko.name || ko.imageUrl));
}

export function cardPrintedInLanguage(
  card: Pick<RiftboundCard, 'language' | 'locales'>,
  scope: CollectionLanguageScope,
): boolean {
  if (scope === 'all') return true;
  if (scope === 'zh') return card.language === 'zh' || cardHasChineseFace(card);
  if (scope === 'ko') return card.language === 'ko' || cardHasKoreanFace(card);
  return card.language !== 'zh' && card.language !== 'ko';
}

/**
 * Copies the collection page should count for this language.
 * `all` is the total. English is everything that is not a foreign lot.
 * A card that is itself a Chinese or Korean printing counts only in that language.
 */
export function scopedCopyQuantity(
  card: Pick<RiftboundCard, 'language'>,
  entry: Pick<CollectionEntry, 'quantity' | 'lots'> | undefined,
  scope: CollectionLanguageScope,
): number {
  if (!entry || entry.quantity <= 0) return 0;
  if (scope === 'all') return entry.quantity;
  if (card.language === 'zh' || card.language === 'ko') {
    return scope === card.language ? entry.quantity : 0;
  }
  if (scope === 'zh' || scope === 'ko') return languageLotQuantity(entry.lots, scope);
  return englishCopyQuantity(entry);
}

/**
 * Face to draw on a collection tile. Absent means the English catalog image.
 * 中文 and 한국어 always use that face. All uses one only when every copy is that language.
 */
export function collectionTileLocale(
  scope: CollectionLanguageScope,
  card: Pick<RiftboundCard, 'language' | 'locales'>,
  entry: Pick<CollectionEntry, 'quantity' | 'lots'> | undefined,
): 'zh' | 'ko' | undefined {
  if (card.language === 'zh' || card.language === 'ko') return undefined;
  if (scope === 'zh') return cardHasChineseFace(card) ? 'zh' : undefined;
  if (scope === 'ko') return cardHasKoreanFace(card) ? 'ko' : undefined;
  if (scope === 'en') return undefined;
  const quantity = entry?.quantity ?? 0;
  if (quantity <= 0) return undefined;
  if (cardHasChineseFace(card) && languageLotQuantity(entry?.lots, 'zh') === quantity) return 'zh';
  if (cardHasKoreanFace(card) && languageLotQuantity(entry?.lots, 'ko') === quantity) return 'ko';
  return undefined;
}

export interface ScopedQuantityPlan {
  quantity: number;
  quantityFoil: number;
  lots: CollectionLot[] | undefined;
  /** No copies remain in any language. */
  remove: boolean;
}

function foilQuantity(lots: readonly CollectionLot[]): number {
  return lots.reduce((sum, lot) => sum + (lot.foil ? lotQuantity(lot) : 0), 0);
}

function resizeTrackedLots(
  lots: CollectionLot[],
  next: number,
  language: 'zh' | 'ko' | undefined,
  foilOnly: boolean,
): CollectionLot[] {
  if (next <= 0) return [];
  if (language && lots.length === 0) {
    return [{ quantity: next, language, ...(foilOnly ? { foil: true } : {}) }];
  }
  if (language && lots.length === 1) {
    return [{ ...lots[0], quantity: next, language, ...(foilOnly ? { foil: true } : {}) }];
  }
  if (sumLots(lots) <= next) return lots.map((lot) => ({ ...lot }));
  const foilCap = foilOnly ? next : Math.min(foilQuantity(lots), next);
  return clampLotsToQuantities(lots, next, foilCap) ?? [];
}

/**
 * Apply the quantity shown on the collection pill.
 * English, 中文, and 한국어 edit that language. All edits the default language
 * for new copies, and leaves the other languages in place.
 */
export function planViewQuantityChange({
  card,
  entry,
  foilOnly,
  view,
  defaultLanguage,
  nextDisplayed,
}: {
  card: Pick<RiftboundCard, 'language'>;
  entry: Pick<CollectionEntry, 'quantity' | 'quantityFoil' | 'lots'> | undefined;
  foilOnly: boolean;
  view: CollectionLanguageScope;
  /** Language a new copy gets while All is showing every copy. */
  defaultLanguage: CollectionCopyLanguage;
  nextDisplayed: number;
}): ScopedQuantityPlan {
  const writeScope = view === 'all' ? defaultLanguage : view;
  const displayed = view === 'all' ? (entry?.quantity ?? 0) : scopedCopyQuantity(card, entry, view);
  const currentWrite = scopedCopyQuantity(card, entry, writeScope);
  const next = view === 'all'
    ? Math.max(0, currentWrite + (nextDisplayed - displayed))
    : nextDisplayed;
  return applyScopedQuantity({ entry, foilOnly, scope: writeScope, next });
}

/**
 * Set one language's copy count. English stays the unmarked remainder.
 * A Chinese or Korean count lands on that language's lot and leaves other copies in place.
 */
export function applyScopedQuantity({
  entry,
  foilOnly,
  scope,
  next,
}: {
  entry: Pick<CollectionEntry, 'quantity' | 'quantityFoil' | 'lots'> | undefined;
  foilOnly: boolean;
  scope: CollectionLanguageScope;
  next: number;
}): ScopedQuantityPlan {
  const target = Math.max(0, Math.floor(next) || 0);
  const lots = entry?.lots ?? [];
  const foreign = lots.filter(isForeignLot).map((lot) => ({ ...lot }));
  const english = lots.filter((lot) => !isForeignLot(lot)).map((lot) => ({ ...lot }));
  const foreignQty = sumLots(foreign);
  const quantity = entry?.quantity ?? 0;

  let nextLots: CollectionLot[];
  let total: number;
  if (scope === 'zh' || scope === 'ko') {
    const tracked = foreign.filter((lot) => lot.language === scope);
    const otherForeign = foreign.filter((lot) => lot.language !== scope);
    nextLots = [
      ...otherForeign,
      ...english,
      ...resizeTrackedLots(tracked, target, scope, foilOnly),
    ];
    total = Math.max(0, quantity - sumLots(tracked)) + target;
  } else if (scope === 'en') {
    nextLots = [...foreign, ...resizeTrackedLots(english, target, undefined, foilOnly)];
    total = foreignQty + target;
  } else {
    const totalKept = Math.max(target, foreignQty);
    const englishTarget = totalKept - foreignQty;
    nextLots = [...foreign, ...resizeTrackedLots(english, englishTarget, undefined, foilOnly)];
    total = totalKept;
  }

  const kept = nextLots.filter((lot) => lotQuantity(lot) > 0);
  const quantityFoil = foilOnly
    ? total
    : Math.min(entry?.quantityFoil ?? 0, total);
  return {
    quantity: total,
    quantityFoil,
    lots: kept.length ? kept : undefined,
    remove: total <= 0,
  };
}
